'use client';

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';
import { cachedRequest, invalidateAllCache } from '@/lib/supabase/request-cache';
import { absoluteSiteUrl } from '@/lib/site-url';
import { resolveRoleFromProfile } from '@/lib/auth/resolve-role';
import { useIdleSignOut, resetIdleTracker } from '@/lib/hooks/useIdleSignOut';

export type UserRole =
  | 'admin'
  | 'teacher'
  | 'family'
  | 'teacher_checkin'
  | 'ops_admin'
  | 'super_admin'
  | null;

// Role tier helpers — exported so middleware and guards can use them
export const ADMIN_ROLES: UserRole[] = ['admin', 'teacher_checkin', 'ops_admin', 'super_admin'];
export const OPS_ADMIN_ROLES: UserRole[] = ['admin', 'ops_admin', 'super_admin'];
export const SUPER_ADMIN_ROLES: UserRole[] = ['admin', 'super_admin'];
export const TEACHER_CHECKIN_ROLES: UserRole[] = [
  'admin',
  'teacher_checkin',
  'ops_admin',
  'super_admin',
];

interface AuthContextValue {
  user: any;
  session: any;
  loading: boolean;
  role: UserRole;
  /**
   * True when `role` came from the AUTHORITATIVE user_profiles query,
   * not from the 3-second timeout fallback. Anything that routes based
   * on role (RootPage redirect, middleware-mirror logic, etc.) must
   * wait until this is true — otherwise a slow first-paint query
   * bounces admins to /client-portal.
   */
  roleConfirmed: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isOpsAdmin: boolean;
  isTeacherCheckin: boolean;
  isTeacher: boolean;
  isFamily: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

async function fetchUserRole(
  supabase: ReturnType<typeof createClient>,
  userId: string,
  userEmail: string
): Promise<UserRole> {
  return cachedRequest(
    `role:${userId}`,
    async () => {
      const role = await resolveRoleFromProfile(supabase, userId, userEmail);
      return role as UserRole;
    },
    10 * 60_000
  );
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<UserRole>(null);
  // True once the AUTHORITATIVE user_profiles role has resolved.
  // Stays false while the 3-second timeout fallback is the current
  // value of `role`. Consumers that route based on role (e.g.
  // RootPage redirect) MUST wait on this before navigating, or
  // they'll send admins to /client-portal during slow first-paint.
  const [roleConfirmed, setRoleConfirmed] = useState(false);

  const supabaseRef = useRef(createClient());

  useEffect(() => {
    const supabase = supabaseRef.current;
    let mounted = true;

    const applySession = async (nextSession: any, options?: { refreshOnly?: boolean }) => {
      if (!mounted) return;

      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (!nextSession?.user) {
        setRole(null);
        setRoleConfirmed(false);
        return;
      }

      if (options?.refreshOnly) return;

      // Fresh session means the previously-confirmed role (if any) is
      // stale until the new user_profiles query resolves.
      setRoleConfirmed(false);

      // 3s ceiling on the role lookup — a slow user_profiles query must not
      // prevent the app from rendering. We optimistically assume 'family' on
      // timeout, BUT we also keep waiting on the real query and correct the
      // state (and confirm it) once it resolves. Consumers that route based
      // on role MUST gate on `roleConfirmed` so a slow query doesn't bounce
      // admins to /client-portal during first paint.
      const realRolePromise = fetchUserRole(
        supabase,
        nextSession.user.id,
        nextSession.user.email ?? ''
      );
      const roleTimeout = new Promise<UserRole>((resolve) =>
        setTimeout(() => resolve('family'), 3000)
      );
      const nextRole = await Promise.race([realRolePromise, roleTimeout]);
      if (mounted) setRole(nextRole);

      // Keep waiting on the real promise. When it lands, sync `role`
      // (if different) AND mark `roleConfirmed=true` so routing code
      // can finally make its decision.
      realRolePromise
        .then((realRole) => {
          if (!mounted) return;
          if (realRole !== nextRole) setRole(realRole);
          setRoleConfirmed(true);
        })
        .catch(() => {
          // fetchUserRole returns 'family' on error rather than rejecting.
          // Confirm anyway so routing isn't blocked forever on a downstream
          // failure.
          if (mounted) setRoleConfirmed(true);
        });
    };

    // bootstrapSession guarantees setLoading(false) via finally — even if applySession
    // throws or the role fetch fails. A 3-second hard ceiling on getSession()
    // prevents a hung network call from holding the app in a loading spinner on
    // hard reset. If the session can't be retrieved in time we proceed as
    // signed-out; onAuthStateChange will promote to signed-in as soon as the
    // real session arrives.
    const bootstrapSession = async () => {
      try {
        const sessionPromise = supabase.auth.getSession();
        const timeout = new Promise<{ data: { session: null } }>((resolve) =>
          setTimeout(() => resolve({ data: { session: null } }), 3000)
        );
        const result = await Promise.race([sessionPromise, timeout]);
        const initialSession = (result as any)?.data?.session ?? null;
        await applySession(initialSession);
      } catch {
        // Never let bootstrap throw — failing open is always better than
        // leaving the whole app stuck on a spinner.
      } finally {
        if (mounted) setLoading(false);
      }
    };

    bootstrapSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, nextSession: Session | null) => {
        if (!mounted) return;

        if (event === 'SIGNED_OUT' || !nextSession) {
          // Supabase may fire SIGNED_OUT from a token refresh failure or
          // external tab sign-out. Either way, purge everything.
          invalidateAllCache();
          setSession(null);
          setUser(null);
          setRole(null);
          setRoleConfirmed(false);
          setLoading(false);
          return;
        }

        await applySession(nextSession, { refreshOnly: event === 'TOKEN_REFRESHED' });
        if (mounted) setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabaseRef.current.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: absoluteSiteUrl('/auth/callback'),
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabaseRef.current.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    // Wipe the ENTIRE in-memory request cache before tearing down the
    // session. A narrow prefix-based invalidation left lesson/schedule
    // entries visible to the next signed-in user on the same browser.
    invalidateAllCache();
    resetIdleTracker();

    // Clear any transient in-memory state we own BEFORE Supabase signs out,
    // so that the auth-state listener firing SIGNED_OUT doesn't race with
    // components that still think they have a user.
    setRole(null);
    setRoleConfirmed(false);

    const { error } = await supabaseRef.current.auth.signOut();
    if (error) throw error;
  };

  // Idle timeout. Admins handle sensitive data, so they get a tighter
  // window (2h). Families on personal devices get longer (8h). Keyed
  // off the confirmed role to avoid kicking out a brand-new session
  // mid-bootstrap.
  const isAdminish = ADMIN_ROLES.includes(role);
  const idleMs = isAdminish ? 2 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000;
  const handleIdleSignOut = useCallback(() => {
    // Best-effort — fire and forget. signOut handles its own errors.
    signOut().catch(() => {
      /* noop */
    });
  }, []); // signOut closes over state but doesn't need re-binding
  useIdleSignOut({
    enabled: !!user && roleConfirmed,
    idleMs,
    onTimeout: handleIdleSignOut,
  });

  const getCurrentUser = async () => {
    const {
      data: { user: u },
      error,
    } = await supabaseRef.current.auth.getUser();
    if (error) throw error;
    return u;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async () => {
    if (!user) return null;
    return cachedRequest(
      `profile:${user.id}`,
      async () => {
        const { data, error } = await supabaseRef.current
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        if (error) throw error;
        return data;
      },
      5 * 60_000
    );
  };

  const value: AuthContextValue = {
    user,
    session,
    loading,
    role,
    roleConfirmed,
    isAdmin: ADMIN_ROLES.includes(role),
    isSuperAdmin: SUPER_ADMIN_ROLES.includes(role),
    isOpsAdmin: OPS_ADMIN_ROLES.includes(role),
    isTeacherCheckin: TEACHER_CHECKIN_ROLES.includes(role),
    isTeacher: role === 'teacher',
    isFamily: role === 'family',
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:312:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="312" data-component-end-line="312" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
};
